import {FontIcon, SearchBox, useTheme} from '@fluentui/react';
import {useOutsideClick} from 'hooks';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
	Location,
	useLocation,
	useMatch,
	useNavigate,
	useSearchParams,
} from 'react-router-dom';
import {
	PogisDocument,
	Regulation,
	RegulatoryDocument,
	Requirement,
} from 'types';
import {
	useGetSuggestionsAiLazyQuery,
	useGetSuggestionsPogisLazyQuery,
	useGetSuggestionsRegulationsLazyQuery,
	useGetSuggestionsRegulatoryDocumentLazyQuery,
	useGetSuggestionsRequirementsLazyQuery,
} from './hooks/useQuickSearch.generated';
import {getClassNames, TextSearchBarSuggestions} from './TextSearchSuggestions';
import {PAGE_PATHS} from 'pages';

const NUM_TOP_SUGG = 5;
const NUM_MIN_SEARCH_CHAR = 3;
const DELAY_USER_INPUT = 500;

type Suggestion = {
	display: JSX.Element;
	link: string;
	id?: string;
	searchText?: string;
	newTab?: boolean;
	page?: string;
};

const nsTranslation = 'components/textsearchbar';

export const TextSearchBar: React.FC = () => {
	const {t} = useTranslation(nsTranslation);

	const {t: tPage} = useTranslation(nsTranslation, {
		keyPrefix: 'Pages',
	});

	const [searchParams] = useSearchParams();

	const navigate = useNavigate();
	const location = useLocation();
	const theme = useTheme();
	const mtcPogis = useMatch('/pogisArchive');
	const mtcRegulation = useMatch(PAGE_PATHS.regulations);
	const mtcRequirement = useMatch(PAGE_PATHS.requirements);
	const mtcReport = useMatch('/reports/regulatory-documents');
	const mtcSearch = useMatch('/search');
	const mtcRegDoc = useMatch(
		'/regulations/:regulationId/:regulatoryDocumentId/paragraphs',
	);
	const mtcRegDocShort = useMatch('/regulatoryDocuments/:regulatoryDocumentId');

	const classNames = getClassNames(theme);

	const [
		getSuggestionsAI,
		{data: dataSuggestions, loading: loadingSuggestions},
	] = useGetSuggestionsAiLazyQuery();

	const [
		getSuggestionsReq,
		{data: dataSuggestionsReq, loading: loadingSuggestionsReq},
	] = useGetSuggestionsRequirementsLazyQuery();

	const [
		getSuggestionsReg,
		{data: dataSuggestionsReg, loading: loadingSuggestionsReg},
	] = useGetSuggestionsRegulationsLazyQuery();

	const [
		getSuggestionsRegDoc,
		{data: dataSuggestionsRegDoc, loading: loadingSuggestionsRegDoc},
	] = useGetSuggestionsRegulatoryDocumentLazyQuery();

	const [
		getSuggestionsPOGIS,
		{data: dataSuggestionsPOGIS, loading: loadingSuggestionsPOGIS},
	] = useGetSuggestionsPogisLazyQuery();

	const [searchText, setSearchText] = useState(
		searchParams.getAll('text').at(0) ?? '',
	);

	const [searchTextDebounced, setSearchTextDebounced] = useState(
		searchParams.getAll('text').at(0) ?? '',
	);

	const [hideSuggestions, setHideSuggestions] = useState(true);

	const [idSuggestion, setIdSuggestion] = useState(0);

	const onOutsideClick = useCallback(() => {
		setHideSuggestions(true);
	}, []);

	const ref = useOutsideClick(onOutsideClick);

	useEffect(() => {
		const handler = setTimeout(() => {
			setSearchTextDebounced(searchText);
		}, DELAY_USER_INPUT);

		return () => {
			clearTimeout(handler);
		};
	}, [searchText]);

	useEffect(() => {
		if (checkSearchValid(searchTextDebounced)) {
			const input = {
				variables: {searchString: searchTextDebounced, limit: NUM_TOP_SUGG},
			};
			getSuggestionsAI(input);
			getSuggestionsReq(input);
			getSuggestionsReg(input);
			getSuggestionsRegDoc(input);
			getSuggestionsPOGIS(input);
		}
	}, [searchTextDebounced]);

	const suggestionsAI: Suggestion[] = useMemo(() => {
		const getSuggestionLinkPattern = (): string => {
			const searchQuery = '?text=#SEARCH_STRING#';
			const basicSearchLink = '/search' + searchQuery;

			if (mtcPogis) {
				return `${basicSearchLink}&entity=pogisdocuments`;
			}
			if (mtcRegulation) {
				return `${basicSearchLink}&entity=regulations`;
			}
			if (mtcRequirement) {
				return `${basicSearchLink}&entity=requirements`;
			}
			if (mtcReport) {
				return `${basicSearchLink}&entity=regulatorydocuments`;
			}
			if (mtcSearch) {
				const entityName: string | null = searchParams.get('entity');
				return `${basicSearchLink}${entityName ? '&entity=' + entityName : ''}`;
			}
			if (mtcRegDoc || mtcRegDocShort) {
				return `${location.pathname}${searchQuery}`;
			}

			return basicSearchLink;
		};

		const suggestionsLinkPattern = getSuggestionLinkPattern();

		const params = {
			searchText,
			isEntity: false,
			items: loadingSuggestions
				? [searchText]
				: [searchText, ...(dataSuggestions?.generateHints ?? [])],
			fnSuggestionDisplay: (x: string) =>
				getSuggestionDisplay('Search', `${x}`),
			fnSuggestionLink: (x: string) =>
				suggestionsLinkPattern.replace('#SEARCH_STRING#', x),
			fnSuggestionSearchText: (x: string) => x,
		};

		return populateSuggestionsArray(params);
	}, [searchText, dataSuggestions, loadingSuggestions, location, searchParams]);

	const suggestionsReq: Suggestion[] = useMemo(() => {
		const params = {
			searchText,
			isEntity: true,
			items: dataSuggestionsReq?.searchRequirements.requirements ?? [],
			fnSuggestionDisplay: (x: Requirement) =>
				getSuggestionDisplay('OpenInNewTab', `${x.requirementId}`),
			fnSuggestionLink: (x: Requirement) => `/requirements/${x.id}`,
			fnSuggestionSearchText: (x: Requirement) => searchText,
			newTab: true,
		};

		return populateSuggestionsArray(params);
	}, [searchText, dataSuggestionsReq]);

	const suggestionsReg: Suggestion[] = useMemo(() => {
		const params = {
			searchText,
			isEntity: true,
			items: dataSuggestionsReg?.searchRegulations.regulations ?? [],
			fnSuggestionDisplay: (x: Regulation) =>
				getSuggestionDisplay('OpenInNewTab', x.regulationNumber),
			fnSuggestionLink: (x: Regulation) => `/regulations/${x.id}`,
			fnSuggestionSearchText: (x: Regulation) => searchText,
			newTab: true,
		};

		return populateSuggestionsArray(params);
	}, [searchText, dataSuggestionsReg]);

	const suggestionsRegDoc: Suggestion[] = useMemo(() => {
		const params = {
			searchText,
			isEntity: true,
			items:
				dataSuggestionsRegDoc?.searchRegulatoryDocuments.regulatoryDocuments ??
				[],
			fnSuggestionDisplay: (x: RegulatoryDocument) =>
				getSuggestionDisplay(
					'OpenInNewTab',
					`${x.regulation?.regulationNumber} | ${x.name}`,
				),
			fnSuggestionLink: (x: RegulatoryDocument) =>
				`/regulatoryDocuments/${x.id}`,
			fnSuggestionSearchText: (x: RegulatoryDocument) => searchText,
			newTab: true,
		};

		return populateSuggestionsArray(params);
	}, [searchText, dataSuggestionsRegDoc]);

	const suggestionsPOGIS: Suggestion[] = useMemo(() => {
		const params = {
			searchText,
			isEntity: true,
			items: dataSuggestionsPOGIS?.searchPogisDocuments.pogisDocuments ?? [],
			fnSuggestionDisplay: (x: PogisDocument) =>
				getSuggestionDisplay('OpenInNewTab', `${x.pogisId}`),
			fnSuggestionLink: (x: PogisDocument) => `/pogisArchive?id=${x.pogisId}`,
			fnSuggestionSearchText: (x: PogisDocument) => searchText,
			newTab: true,
		};

		return populateSuggestionsArray(params);
	}, [searchText, dataSuggestionsPOGIS]);

	const suggestionsFooter: Suggestion[] = useMemo(() => {
		return [
			{
				display: getSuggestionDisplay(
					'BulletedTreeList',
					tPage('Requirements'),
				),
				link: `/requirements`,
				page: tPage('Requirements'),
			},
			{
				display: getSuggestionDisplay('BulletedTreeList', tPage('Regulations')),
				link: `/regulations`,
				page: tPage('Regulations'),
			},
			{
				display: getSuggestionDisplay(
					'BulletedTreeList',
					tPage('RegulatoryDocuments'),
				),
				link: `/reports/regulatory-documents`,
				page: tPage('RegulatoryDocuments'),
			},

			{
				display: getSuggestionDisplay('BulletedTreeList', tPage('Pogis')),
				link: `/pogisArchive`,
				page: tPage('Pogis'),
			},
		];
	}, []);

	const suggestionsAll: Suggestion[] = useMemo(
		() =>
			[
				suggestionsAI,
				suggestionsReq,
				suggestionsReg,
				suggestionsRegDoc,
				suggestionsPOGIS,
				suggestionsFooter,
			].flat(),
		[
			searchText,
			suggestionsAI,
			suggestionsReq,
			suggestionsReg,
			suggestionsRegDoc,
			suggestionsPOGIS,
			suggestionsFooter,
		],
	);

	const selectedSuggestion = useMemo(
		() => suggestionsAll.at(idSuggestion),
		[suggestionsAll, idSuggestion],
	);

	const memoSearchBox = useMemo(() => {
		const handleSearchRemoveFocus = () => {
			const inputElement = document.querySelector(
				'input[role="searchbox"]',
			) as HTMLInputElement | null;
			if (inputElement) {
				inputElement.blur();
			}
		};

		const onSearch = (text: string) => {
			navigate(selectedSuggestion?.link ?? '');
			setSearchText(selectedSuggestion?.searchText ?? '');
			setHideSuggestions(true);
			handleSearchRemoveFocus();
		};

		const handleChange = (_: any, val?: string) => {
			const searchString = val ?? '';
			setSearchText(searchString);
			setHideSuggestions(false);
			setIdSuggestion(0);
		};

		const handleClear = () => {
			setSearchText('');
			setIdSuggestion(0);
			if (mtcRegDoc || mtcRegDocShort) {
				setHideSuggestions(true);
				navigate(location.pathname);
				handleSearchRemoveFocus();
			} else {
				setHideSuggestions(false);
			}
		};

		const handleFocus = () => {
			setHideSuggestions(false);
			setIdSuggestion(0);
		};

		const handleKeyDown: React.KeyboardEventHandler<
			HTMLInputElement
		> = event => {
			if (event.key === 'ArrowUp') {
				if (idSuggestion >= 1) {
					setIdSuggestion(idSuggestion - 1);
				}
			}
			if (event.key === 'ArrowDown') {
				if (idSuggestion < suggestionsAll.length - 1) {
					setIdSuggestion(idSuggestion + 1);
				}
			}
			setHideSuggestions(false);
		};
		return (
			<SearchBox
				key='searchbox'
				placeholder={t('SearchPlaceholder')}
				value={searchText}
				onSearch={onSearch}
				onChange={handleChange}
				onClear={handleClear}
				onFocus={handleFocus}
				onKeyDown={handleKeyDown}
				styles={{
					root: {
						position: 'relative',
					},
				}}
			/>
		);
	}, [
		searchText,
		hideSuggestions,
		suggestionsAll,
		idSuggestion,
		selectedSuggestion,
	]);

	const propTextSearchBarSuggestions = {
		searchText,
		loadingSuggestionsReq,
		loadingSuggestionsReg,
		loadingSuggestionsRegDoc,
		loadingSuggestionsPOGIS,
		suggestionsAI,
		suggestionsReq,
		suggestionsReg,
		suggestionsRegDoc,
		suggestionsPOGIS,
		suggestionsFooter,
		selectedSuggestion,
		setHideSuggestions,
		setSearchText,
	};

	return (
		<div className={classNames.componentWrapper} ref={ref}>
			{memoSearchBox}
			{hideSuggestions === false && (
				<TextSearchBarSuggestions {...propTextSearchBarSuggestions} />
			)}
		</div>
	);
};

export const checkSearchValid = (searchText: string) =>
	searchText.trim().length >= NUM_MIN_SEARCH_CHAR;

const populateSuggestionsArray = (params: {
	searchText: string;
	isEntity: boolean;
	items: any[];
	fnSuggestionDisplay: (item: any) => JSX.Element;
	fnSuggestionLink: (item: any) => string;
	fnSuggestionSearchText: (item: any) => string;
	newTab?: boolean;
}) => {
	const {
		searchText,
		isEntity,
		items,
		newTab,
		fnSuggestionDisplay,
		fnSuggestionLink,
		fnSuggestionSearchText,
	} = params;
	return checkSearchValid(searchText)
		? items.slice(0, NUM_TOP_SUGG).map(x => {
				return {
					display: fnSuggestionDisplay(x),
					link: fnSuggestionLink(x),
					searchText: fnSuggestionSearchText(x),
					newTab,
					id: isEntity ? x.id : null,
				};
		  })
		: [];
};

const getSuggestionDisplay = (iconName: string, searchText: string) => (
	<>
		<FontIcon iconName={iconName} />
		<span style={{paddingLeft: 10}}>{searchText}</span>
	</>
);
